/**
 * ================================================================
 * FILE METADATA & OVERVIEW
 * ================================================================
 *
 * @fileOverview     Route change scroll restoration utility component.
 * @module           navigation/ScrollToTop
 * @version          1.1.0
 * @since            2025-10-01
 * @author           Auto Generated <dev@ap.local>
 * @lastModified     2025-10-01
 *
 * @purpose          Ensures that on navigation (pathname change) the primary app
 *                   scroll container returns to the top. The application uses an
 *                   internal scrollable <main> instead of relying on window scroll,
 *                   so this component targets the element with id `app-scroll-container`.
 *                   Provides graceful fallbacks & timing strategies to avoid race
 *                   conditions with Suspense / animations.
 * @dependencies     React (useEffect), react-router (useLocation)
 * @exports          ScrollToTop - headless utility component
 *
 * @architecture
 * - Pattern: Headless utility component with side-effect only
 * - Strategy: Double rAF + micro delay to ensure layout is painted
 * - Robustness: Multi-selector fallback & retry mechanism
 *
 * @performance      Minimizes layout thrash by batching inside rAF; only runs on pathname change.
 * @accessibility    Helps maintain expected UX: new route = top of content.
 * @testing          Can be tested by mocking useLocation & spying on scrollTo.
 *
 * ================================================================
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Resolve the primary scroll container.
 * Attempts multiple fallbacks to remain resilient to future layout changes.
 */
function resolveScrollContainer(): HTMLElement | null {
  return (
    document.getElementById('app-scroll-container') ||
    (document.querySelector('[data-scroll-container]') as HTMLElement | null) ||
    (document.querySelector('main#app-scroll-container') as HTMLElement | null) ||
    (document.querySelector('main[role="main"]') as HTMLElement | null) ||
    (document.querySelector('main') as HTMLElement | null)
  );
}

/**
 * Scroll the target container (or window) to top.
 * Uses 'auto' behavior to avoid unsupported custom values.
 */
function performScroll() {
  const target = resolveScrollContainer();
  try {
    if (target) {
      target.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  } catch {
    // Silently ignore; scroll restoration is best-effort.
  }
}

/**
 * ================================================================
 * REACT COMPONENT: ScrollToTop
 * ================================================================
 *
 * @component        ScrollToTop
 * @description      On every pathname change, force scroll position to the top of the primary container.
 * @category         Utility Component
 *
 * @hooks
 * - useLocation: Listen to route (pathname) changes
 * - useEffect: Execute scroll side-effect after paint
 *
 * @usage
 *  <ScrollToTop />
 * ================================================================
 */
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Strategy: double rAF to allow React layout + Framer Motion transitions.
    const frame1 = requestAnimationFrame(() => {
      const frame2 = requestAnimationFrame(() => {
        performScroll();
        // Secondary micro retry in case Suspense content mounted slightly after.
        const timeout = window.setTimeout(performScroll, 60);
        // Cleanup for timeout.
        return () => window.clearTimeout(timeout);
      });
      // Cleanup inner frame if outer effect unmounts quickly.
      return () => cancelAnimationFrame(frame2);
    });
    // Cleanup outer frame.
    return () => cancelAnimationFrame(frame1);
  }, [pathname]);

  return null;
}

export default ScrollToTop;

/**
 * ================================================================
 * END OF FILE: ScrollToTop.tsx
 * ================================================================
 * @summary          Provides reliable route-based scroll reset for internal scroll container.
 * @exports          ScrollToTop
 * @dependencies     React, react-router-dom
 * @usedBy           Layout component
 * @changelog
 * - 2025-10-01: Added robust container resolution & double rAF strategy.
 * - 2025-10-01: Added documentation header & footer per standards.
 * @todos
 * - [ ] Consider observing container size changes for future dynamic adjustments.
 * ================================================================
 */
